import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, Slide } from '@mui/material';

const SnackbarContext = createContext();

const SlideTransition = (props) => {
  return <Slide {...props} direction="down" />;
};

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success' | 'error' | 'warning' | 'info'
    autoHideDuration: 5000,
  });

  const showSnackbar = useCallback((message, severity = 'info', autoHideDuration = 5000) => {
    // Don't show snackbar if message is empty or undefined
    if (!message || message.trim() === '') {
      return;
    }

    setSnackbar({
      open: true,
      message,
      severity,
      autoHideDuration,
    });
  }, []);

  // Listen for custom events from snackbarService
  React.useEffect(() => {
    const handleShowSnackbar = (event) => {
      const { message, severity, autoHideDuration } = event.detail;
      // Validation is handled inside showSnackbar function
      showSnackbar(message, severity, autoHideDuration);
    };

    window.addEventListener('show-snackbar', handleShowSnackbar);

    return () => {
      window.removeEventListener('show-snackbar', handleShowSnackbar);
    };
  }, [showSnackbar]);

  const handleClose = (_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            fontSize: '14px',
            boxShadow: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
